import { createSelector } from 'reselect'
import { takeEvery, takeLatest, select, take, call, all, put, delay } from 'redux-saga/effects'

import {
    map, reduce, filter, forEach, get, omit, pick, keys, values, flatMap, compact, concat, overEvery,
    noop, join, split, startCase, includes, isFunction, isObject, isEmpty
} from 'lodash'

import { parseISO, isDate, isSameDay, startOfDay, endOfDay } from 'date-fns'
import URI from 'urijs'

import { utils, i18n } from 'parking-react-common'

import { routing, app } from '../redux'

export const TYPES = {
    dateRange: 'dateRange'
}


//
//  ACTIONS

export const OPENED  = '@ filters / OPENED'
export const TOGGLED = '@ filters / TOGGLED'
export const SET     = '@ filters / SET'
export const UNSET   = '@ filters / UNSET'
export const ENABLED = '@ filters / ENABLED'

export const open = () => ({ type: OPENED })
export const toggle = () => ({ type: TOGGLED })
export const set = (name, type, value) => ({ type: SET, payload: { name, type, value } })
export const unset = (name, type) => ({ type: UNSET, payload: { name, type } })
export const enable = (keys) => ({ type: ENABLED, payload: keys })


//
//  REDUCER

export const intialState = {
    isOpen    : false,
    active    : {},
    available : {}
}

export function reducer(state = intialState, action = {}) {
    switch (action.type) {
        case OPENED:
            return {
                ...state,
                isOpen: true
            }

        case TOGGLED: {
            return {
                ...state,
                isOpen: !state.isOpen
            }
        }

        case SET: {
            const { name, type, value } = action.payload
            return {
                ...state,
                active: {
                    ...state.active,
                    [toKey(name, type)]: value
                }
            }
        }

        case UNSET: {
            const { name, type } = action.payload
            const key = toKey(name, type)
            return {
                ...state,
                active: omit(state.active, key)
            }
        }

        case ENABLED: {
            return {
                ...state,
                available: action.payload
            }
        }

        default:
            return state
    }
}

//
//  SAGA

export function* saga() {
    yield takeLatest([SET, UNSET, OPENED, TOGGLED], updateAddressWithFilterParams)
    yield takeEvery(app.INITIALIZED, readFiltersFromAddress)
}

function* updateAddressWithFilterParams() {
    yield delay(500)

    const isPanelOpen = yield select(isOpen)
    const activeParams = yield select(getActiveParams)
    const availableParamNames = yield select(getAvailableParamNames)
    const currentRoute = yield select(routing.getCurrentRoute)

    const newUrl = new URI(currentRoute.url)

    isPanelOpen
        ? newUrl.addSearch('show_filters', 1)
        : newUrl.removeSearch('show_filters')

    forEach(availableParamNames, (param) => newUrl.removeSearch(param))
    forEach(activeParams, (value, param) => newUrl.addSearch(param, value))

    routing.replace(newUrl.toString()) // eslint-disable-line lodash/prefer-lodash-method
}

function* readFiltersFromAddress() {
    const currentRoute = yield select(routing.getCurrentRoute)

    if (isEmpty(currentRoute)) {
        yield take(routing.ROUTE_CHANGED)
        yield call(readFiltersFromAddress)
        return
    }

    const { params } = currentRoute
    const availableFilters = yield select(getAvailable)

    const filtersToSet = compact(map(availableFilters, (type, name) => {
        const currentFilterParamNames = paramNames(name, type)
        const validFilterParams = pick(params, currentFilterParamNames)
        if (!isEmpty(validFilterParams)) {
            const value = parseParam(name, type, validFilterParams)
            return { name, type, value }
        }
        return null
    }))

    yield all(map(filtersToSet, ({ name, type, value }) => put(set(name, type, value))))

    if (get(params, 'show_filters') === '1') {
        yield put(open())
    }
}


//
//  HELPERS and UTILITIES

const KEY_SEPARATOR = '__'

export function toKey(name, value) {
    return join([name, value], KEY_SEPARATOR)
}

export function parseKey(key) {
    const [name, type] = split(key, KEY_SEPARATOR)
    return { name, type }
}

export function asParam(name, type, value) {
    switch (type) {
        case TYPES.dateRange: {
            const { startDate, endDate } = value

            return {
                [`${name}__gte`] : utils.formatAPIDate(startDate),
                [`${name}__lte`] : utils.formatAPIDate(endDate)
            }
        }

        default:
            return { [name]: toString(value) }
    }
}

export function asPredicate(name, type, value) {
    switch (type) {
        case TYPES.dateRange: {
            const { startDate, endDate } = value
            return (input) => {
                const date = isDate(input[name]) ? input[name] : parseISO(input[name])
                return date >= startOfDay(startDate) && date <= endOfDay(endDate)
            }
        }

        default:
            return noop
    }
}

export function asLabel(name, type, value) {
    return `${i18n.t(startCase(name))}: ${humanize(name, value, type)}`
}

export function humanize(name, value, type) {
    switch (type) {
        case TYPES.dateRange: {
            if (!isObject(value)) {
                return null
            }
            const { startDate, endDate } = value
            if (isSameDay(startDate, endDate)) {
                return utils.formatPrettyDate(startDate)
            }
            return join(map([startDate, endDate], utils.formatPrettyDate), ' - ')
        }

        default:
            return null
    }
}

export function paramNames(name, type) {
    switch (type) {
        case TYPES.dateRange: {
            return [`${name}__gte`, `${name}__lte`]
        }

        default:
            return [name]
    }
}

export function parseParam(name, type, params) {
    switch (type) {
        case TYPES.dateRange: {
            return {
                startDate : parseISO(get(params, `${name}__gte`)),
                endDate   : parseISO(get(params, `${name}__lte`))
            }
        }

        default:
            return get(params, name)
    }
}


//
//  SELECTORS

export const getState = (state) => (state.filters)

export const isOpen = createSelector(
    getState,
    (state) => !!state.isOpen
)
export const getAvailable = createSelector(
    getState,
    (state) => state.available
)
export const getAllActive = createSelector(
    getState,
    (state) => state.active
)
export const getValue = (name, type) => createSelector(
    getActive,
    (active) => get(active, toKey(name, type))
)
export const getActive = createSelector(
    [getAllActive, getAvailable],
    (active, available) => {
        const names = keys(available)
        const types = values(available)
        return reduce(active, (acc, value, key) => {
            const { name, type } = parseKey(key)
            if (!includes(names, name) || !includes(types, type)) {
                return acc
            }

            return {
                ...acc,
                [key]: value
            }
        }, {})
    }
)
export const getActiveParams = createSelector(
    getActive,
    (active) => reduce(active, (acc, rawValue, key) => {
        const { name, type } = parseKey(key)
        const value = asParam(name, type, rawValue)

        if (!isObject(value)) {
            return acc
        }

        return {
            ...acc,
            ...value
        }
    }, {})
)
export const getActivePredicates = createSelector(
    getActive,
    (active) => reduce(active, (acc, rawValue, key) => {
        const { name, type } = parseKey(key)
        const predicate = asPredicate(name, type, rawValue)

        if (!isFunction(predicate)) {
            return acc
        }
        return concat(acc, [predicate])
    }, [])
)
export const getAvailableParamNames = createSelector(
    getAvailable,
    (available) => compact(flatMap(available, (type, name) => paramNames(name, type)))
)
export const apply = (data) => createSelector(
    getActivePredicates,
    (predicates) => {
        const composedPredicates = overEvery(predicates)
        return filter(data, composedPredicates)
    }
)
